// screens-docs.tsx - Docs: the shell, the home page and the one page template
// every documentation page renders through. Content lives in docsContent.tsx.
// Exports: ScreenDocsHome, ScreenDocsPage, DocsShell
import { Fragment, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from '../design/router';
import { Arrow, BurgerIcon, Logo, SearchIcon } from '../components/ui-shared';
import { SectionNavItem } from '../components/SectionNav';
import { ImageSlot } from '../components/ImageSlot';
import { DOC_GROUPS, searchDocs, type DocBlock, type DocPage } from './docsModel';
import { DOC_PAGES } from './docsContent';

export function findDocPage(path: string): DocPage | undefined {
  return DOC_PAGES.find((p) => p.path === path);
}

function DocsShell({ active, children }: { active: string; children: ReactNode }) {
  const [searchQuery, setSearchQuery] = useState('');
  // The 250px sidebar rail doesn't fit a phone width alongside real content;
  // below the breakpoint it becomes a toggled overlay instead (docs-nav-burger
  // in soulstack.css), closing itself once a link is picked.
  const [navOpen, setNavOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  // The search field advertises a ⌘K badge; bind the shortcut it promises.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const q = searchQuery.trim();
  const hits = q ? searchDocs(DOC_PAGES, q) : null;

  return (
    <div className="ss-screen" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="docs-topbar" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24,
        padding: '0 28px', height: 64, borderBottom: '1px solid var(--ss-border-soft)', background: 'var(--ss-bg-2)', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
          <Logo />
          <span className="docs-topbar-label" style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 10, letterSpacing: '0.18em', color: 'var(--ss-text-3)', textTransform: 'uppercase', borderLeft: '1px solid var(--ss-border-soft)', paddingLeft: 14 }}>Docs</span>
        </div>
        <div className="ss-search docs-topbar-search" style={{ flex: 1, maxWidth: 420 }}>
          <SearchIcon />
          <input
            ref={searchRef}
            className="ss-input"
            placeholder="Search docs"
            aria-label="Search docs"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); if (e.target.value.trim()) setNavOpen(true); }}
            style={{ width: '100%', padding: '9px 46px 9px 35px', fontSize: 13.5 }}
          />
          <span className="ss-kbd docs-topbar-kbd">⌘K</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            className="docs-nav-burger"
            aria-label={navOpen ? 'Close docs navigation' : 'Open docs navigation'}
            aria-expanded={navOpen}
            onClick={() => setNavOpen((v) => !v)}
          >
            <BurgerIcon open={navOpen} size={18} />
          </button>
          <Link to="/app" className="ss-btn ss-btn--ghost ss-btn--sm docs-topbar-openstudio">Open studio</Link>
        </div>
      </div>
      <div className="docs-grid" style={{ flex: 1, display: 'grid', gridTemplateColumns: '250px 1fr', minHeight: 0 }}>
        <nav aria-label="Documentation" className={'docs-sidebar' + (navOpen ? ' is-open' : '')} style={{ borderRight: '1px solid var(--ss-border-soft)', background: 'var(--ss-bg-2)', padding: '24px 14px', overflowY: 'auto' }}>
          {hits ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 9.5, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--ss-text-3)', padding: '0 12px 8px' }}>
                {hits.length === 0 ? 'No results' : hits.length === 1 ? '1 result' : hits.length + ' results'}
              </div>
              {hits.length === 0 && (
                <p style={{ fontSize: 12.5, color: 'var(--ss-text-3)', padding: '4px 12px', lineHeight: 1.5 }}>Nothing matches "{q}". Try a different word.</p>
              )}
              {hits.map(({ page, snippet }) => (
                <Link key={page.path} to={page.path} className={'pj-rail-link' + (active === page.path ? ' is-active' : '')} onClick={() => { setNavOpen(false); setSearchQuery(''); }} style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start', height: 'auto' }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{page.title}</span>
                  <span style={{ fontSize: 11.5, color: 'var(--ss-text-3)', lineHeight: 1.45, fontWeight: 400 }}>{snippet}</span>
                </Link>
              ))}
            </div>
          ) : (
            DOC_GROUPS.map((g) => (
              <div key={g} style={{ marginBottom: 22 }}>
                <div style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 9.5, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--ss-text-3)', padding: '0 12px 8px' }}>{g}</div>
                {DOC_PAGES.filter((p) => p.group === g).map((p) => (
                  <SectionNavItem key={p.path} to={p.path} active={active === p.path} style={{ padding: '7px 12px', fontSize: 13 }} onClick={() => setNavOpen(false)}>{p.label}</SectionNavItem>
                ))}
              </div>
            ))
          )}
        </nav>
        <div style={{ overflowY: 'auto' }}>
          <div className="pj-page" style={{ maxWidth: 760 }}>{children}</div>
        </div>
      </div>
    </div>
  );
}

/* ── Docs home ────────────────────────────────────────────────────────── */
export function ScreenDocsHome() {
  const home = DOC_PAGES[0]!;
  const cards = DOC_PAGES.filter((p) => ['/docs/quickstart', '/docs/editor', '/docs/editor/logic', '/docs/publishing', '/docs/multiplayer', '/docs/reference/shortcuts'].includes(p.path));
  return (
    <DocsShell active="/docs">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 32 }}>
        <span className="ss-eyebrow">Documentation</span>
        <h1 className="ss-display" style={{ fontSize: 'clamp(1.7rem, 7vw, 2.5rem)' }}>{home.title}</h1>
        <p style={{ color: 'var(--ss-text-2)', fontSize: 16, maxWidth: 560 }}>{home.summary}</p>
      </div>
      <div className="ss-grid-collapse" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 36 }}>
        {cards.map((c) => (
          <Link key={c.path} to={c.path} className="ss-card" style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 8, color: 'var(--ss-text)' }}>
            <span style={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>{c.label}<Arrow /></span>
            <span style={{ fontSize: 13, color: 'var(--ss-text-3)', lineHeight: 1.5 }}>{c.summary}</span>
          </Link>
        ))}
      </div>
      <div className="doc-prose">
        {home.blocks.map((b, i) => <Block key={i} block={b} />)}
      </div>
      <PrevNext path={home.path} />
    </DocsShell>
  );
}

/* ── The page template ────────────────────────────────────────────────── */
function Block({ block }: { block: DocBlock }) {
  switch (block.kind) {
    case 'p':
      return <p>{block.text}</p>;
    case 'h2':
      return <h2 id={slugify(block.text)}>{block.text}</h2>;
    case 'h3':
      return <h3>{block.text}</h3>;
    case 'ul':
      return <ul>{block.items.map((it, i) => <li key={i}>{it}</li>)}</ul>;
    case 'callout':
      return <div className="doc-callout">{block.text}</div>;
    case 'code':
      return (
        <Fragment>
          <div className="doc-code" style={{ marginBottom: block.caption ? 8 : undefined }}><pre>{block.text}</pre></div>
          {block.caption && <p style={{ fontSize: 13, color: 'var(--ss-text-3)' }}>{block.caption}</p>}
        </Fragment>
      );
    case 'image':
      return <ImageSlot shape="rounded" radius={10} placeholder={block.placeholder} style={{ width: '100%', height: block.height ?? 300, display: 'block', marginBottom: 18 }} />;
    case 'steps':
      return (
        <div style={{ marginBottom: 18 }}>
          {block.items.map((s, i) => (
            <div key={s.title} className="doc-step">
              <span className="doc-step-n">{i + 1}</span>
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 600, margin: '4px 0 8px' }}>{s.title}</h3>
                <div>{s.body}</div>
              </div>
            </div>
          ))}
        </div>
      );
    case 'table':
      return (
        <div className="doc-table-wrap">
          <table className="doc-table">
            <thead><tr>{block.head.map((h) => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {block.rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}
            </tbody>
          </table>
        </div>
      );
  }
}

function slugify(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function PrevNext({ path }: { path: string }) {
  const idx = DOC_PAGES.findIndex((p) => p.path === path);
  const prev = idx > 0 ? DOC_PAGES[idx - 1] : undefined;
  const next = idx >= 0 && idx < DOC_PAGES.length - 1 ? DOC_PAGES[idx + 1] : undefined;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 40, paddingTop: 20, borderTop: '1px solid var(--ss-border-soft)' }}>
      {prev ? (
        <Link to={prev.path} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--ss-text-2)', fontWeight: 600 }}>
          <span style={{ transform: 'rotate(180deg)', display: 'inline-flex' }}><Arrow /></span>{prev.label}
        </Link>
      ) : <span />}
      {next ? (
        <Link to={next.path} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--ss-text-2)', fontWeight: 600, textAlign: 'right' }}>
          {next.label}<Arrow />
        </Link>
      ) : <span />}
    </div>
  );
}

export function ScreenDocsPage({ page }: { page: DocPage }) {
  return (
    <DocsShell active={page.path}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
        <span className="ss-eyebrow">{page.group}</span>
        <h1 className="ss-display" style={{ fontSize: 'clamp(1.6rem, 6vw, 2.125rem)' }}>{page.title}</h1>
        <p style={{ color: 'var(--ss-text-2)', fontSize: 15.5 }}>{page.summary}</p>
      </div>
      <div className="doc-prose">
        {page.blocks.map((b, i) => <Block key={i} block={b} />)}
      </div>
      <PrevNext path={page.path} />
    </DocsShell>
  );
}
